import React, { useEffect, useMemo, useState } from 'react';
import { useGame } from 'context/GameContext';
import Action from '../components/actions/Action';
import { ActionTypes, CityActionTypes } from 'model/actions';
import CityStatus from '../components/CityStatus';
import * as Styled from './City.style';
import RoadActions, { CityActions, isActionVisible, isCityActionVisible } from '../model/actions';
import { getArrival } from '../lib/strings/city';
import Debug from 'debug';
const debug = Debug('pages:City');

/**
 * The `City` component represents a city view in the game.
 *
 * It is responsible for rendering the city's
 * description,
 * available actions, and
 * activities based on the game's current context.
 */
/**
 * The town standing readout is off.
 *
 * It rendered three rows — influence, fear, respect — under the label 'clout', and it arrived
 * already populated: the gate is `progression.totalLevels() >= 3` (skill levels), so the reveal
 * had nothing to do with the thing revealed, and the numbers had been accruing unseen since the
 * first town. Everything else in the game appears *because* the player gained some of it.
 *
 * It also had nothing to show. The thresholds in political/city.js are 85 and 97 against
 * increments of +1 and smaller, so in a game that is a race west the rows never move off Fringe,
 * and the panel omits the one fact that matters — `statusInTown` itself is not in the table.
 *
 * Only the panel is dark. The counters still accrue, passiveControl still registers on a
 * crossing, and the arrival prose (`showStanding` below, strings/city.js) is untouched. Flipping
 * this back on alone will restore the same panel — the gate, the label's register and the
 * thresholds all want fixing together first.
 */
const SHOW_TOWN_STANDING = false;

const City = ({ className }) => {
  const { gameState, setGameState, addNotification } = useGame();
  const { party, event, political, calendar } = gameState;

  const { territory: territoryName, city: cityName } = party;
  const city = political.cities.get(cityName);
  const territory = political.territories.get(territoryName);

  const [activities, setActivities] = useState([]);
  const [actions, setActions] = useState([]);
  const [relation, setRelation] = useState(party.attitude(political));
  const [generosity, setGenerosity] = useState(party.generosity(political));
  const [prices, setPrices] = useState({});
  const [showMarket, setShowMarket] = useState(false);
  const [showStatusInTown, setShowStatusInTown] = useState(false);

  // `party.step` is the road counter, so it is still zero for the whole of the first town and
  // ticks over on the first Depart. Same predicate depart.js uses to tell Depart from Continue.
  const firstTown = !party.started();

  // Unlocked by the horse, same as it always was — phases.resourceListener drops it again if the
  // horse goes. The town only decides where it is drawn, never whether. See departsFromTown.
  const canDepart = isActionVisible(party.phase, ActionTypes.Depart);

  // Picks are hashed off the town name, so this recomputes to the same lines every time. No
  // need to freeze it in state the way the old random description had to be.
  const arrival = useMemo(
    () =>
      getArrival({
        name: city.name,
        territory: territory.name,
        attitude: relation,
        generosity,
        standing: city.calculateStatus(),
        showGenerosity: showMarket,
        showStanding: showStatusInTown,
        firstTown
      }),
    [city, territory, relation, generosity, showMarket, showStatusInTown, firstTown]
  );

  useEffect(() => {
    debug('Attitude', relation, 'generosity', generosity);

    //const season = context.calendar.season();
    const prices = political.prices(party, calendar);
    setPrices(prices);
  }, [relation, generosity, calendar, party, political]);

  useEffect(() => {
    setRelation(party.attitude(political));
    setGenerosity(party.generosity(political));

    // Prices are what the shopkeeper says over the counter, so the board only goes up once the
    // player has stood there. The button being enabled means the post exists, not that it has
    // been walked into.
    setShowMarket(party.history.total(CityActionTypes.TradePost) > 0);
    setShowStatusInTown(party.progression.totalLevels() >= 3);

    setActions(
      Object.keys(CityActions)
        .filter(key => !CityActions[key]({ context: gameState, prices }).isActivity)
        .map(key => {
          const action = CityActions[key]({ context: gameState, prices });
          return {
            ...action,
            isVisible: isCityActionVisible(party.phase, action.index)
          };
        })
    );
    setActivities(
      Object.keys(CityActions)
        .filter(key => CityActions[key]({ context: gameState, prices }).isActivity)
        .map(key => {
          const action = CityActions[key]({ context: gameState, prices });
          return {
            ...action,
            isVisible: isCityActionVisible(party.phase, action.index)
          };
        })
    );
  }, [gameState, party, event, generosity, relation, prices, political]);

  return (
    <Styled.City className={`${className}`}>
      <Styled.Sign>
        <Styled.Name>{arrival.name}</Styled.Name>
        <Styled.Horizon />
      </Styled.Sign>
      <Styled.Territory>{arrival.territory}</Styled.Territory>
      <Styled.Observations>
        {arrival.lines.map(line => (
          <Styled.Observation key={line}>{line}</Styled.Observation>
        ))}
      </Styled.Observations>
      <Styled.ButtonContainer>
        <Styled.Activities>
          {activities &&
            activities.map(
              activity =>
                activity.isVisible && (
                  <Action
                    {...activity}
                    key={activity.index}
                    context={gameState}
                    setContext={setGameState}
                    onNotification={addNotification}
                  />
                )
            )}
        </Styled.Activities>

        <Styled.Actions>
          {actions &&
            actions.map(
              action =>
                action.isVisible && (
                  <Action
                    {...action}
                    key={action.index}
                    context={gameState}
                    setContext={setGameState}
                    onNotification={addNotification}
                  />
                )
            )}

          {showMarket && (
            <CityStatus
              prices={prices}
              showStatusInTown={SHOW_TOWN_STANDING && showStatusInTown}
              city={city}
            />
          )}
        </Styled.Actions>
      </Styled.ButtonContainer>

      {canDepart && (
        <Styled.Departure>
          <Styled.Horizon />
          <Action {...RoadActions.depart} />
        </Styled.Departure>
      )}
    </Styled.City>
  );
};

export default City;
